import { StatusComponentOptions } from 'crono-fe-common/types/StatusOptions';
import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { CronoStatusWrapper, StatusTagButtonWrapper } from './style';
import {
  DefaultCompanyStatusOptions,
  DefaultContactStatusOptions,
} from 'crono-fe-common/types/model';
import { getColorsStatus } from 'crono-fe-common/utils';
import { colors } from 'crono-fe-common/theme';
import { styled, Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import {
  ControlledPopover,
  OverlayTooltip,
  type OverlayPlacement,
  type OverlayProps,
} from 'components/Overlay';
import { cn } from 'utils/style-utils';
import StatusTooltipPicker from './statusTooltipPicker';
import useEditAccount from 'hooks/services/account/useEditAccount';
import useEditProspect from 'hooks/services/prospect/useEditProspect';
import { ExternalValue } from 'crono-fe-common/types/externalValue';
import IconTopArrow from 'crono-fe-common/icons/Icon-Top-Arrow';
import IconBottomArrow from 'crono-fe-common/icons/Icon-Bottom-Arrow';
import { useGetStatusesOptions } from 'utils/businessLogic';
import StatusSettingsIcon from 'crono-fe-common/icons/Icon-Status-Settings';

interface IProps {
  type: StatusComponentOptions | 'lead';
  //If passed the object will call also the API to update the entity
  objectToUpdate?: string;
  currentActualStatus: string | null;
  currentExternalValues: ExternalValue[] | null;
  //This is used when the status is held locally, for example in the status picker in the footer of the tables
  //To this one both actualStatus and option can be passed
  currentPickedStatus?: string | null;
  disabled?: boolean;
  fixedWidth?: boolean;
  nullAsTagPicker?: boolean;

  handleSetStatus?: (status: string) => void;
  placement?: OverlayPlacement;
  /** Pass it when the status sits in a table cell. */
  overlayProps?: OverlayProps;

  style?: CSSProperties;
}

const CronoStatus = ({
  type,
  objectToUpdate,
  currentActualStatus,
  currentExternalValues,
  disabled = false,
  fixedWidth = false,
  nullAsTagPicker = false,
  currentPickedStatus,
  handleSetStatus,
  placement,
  overlayProps,
  style = {},
}: IProps) => {
  const { externalPropertyStatusId, statuses, isExternalPropertyStatus } =
    useGetStatusesOptions(type);

  const currentOption = useMemo(() => {
    if (currentPickedStatus) return currentPickedStatus;
    if (isExternalPropertyStatus) {
      return currentExternalValues?.find(
        (externalValue) =>
          externalValue.externalPropertyId === externalPropertyStatusId,
      )?.value;
    } else {
      return currentActualStatus;
    }
  }, [
    currentPickedStatus,
    currentActualStatus,
    currentExternalValues,
    externalPropertyStatusId,
    isExternalPropertyStatus,
  ]);

  const currentStatus = useMemo(() => {
    return statuses.find((status) => status.option === currentOption);
  }, [statuses, currentOption]);

  const currentColors = useMemo(() => {
    const currentIndex = statuses.findIndex(
      (status) => status.option === currentOption,
    );
    if (currentIndex === -1) return null;
    return getColorsStatus(currentIndex, statuses.length);
  }, [statuses, currentOption]);

  const backgroundColor =
    currentStatus && currentColors
      ? currentColors.dark
      : colors.nullStatusBackground;

  const textColor = currentStatus ? colors.white : colors.nullStatusText;

  const [statusTooltipOpen, setStatusTooltipOpen] = useState(false);
  const handleCloseTooltip = () => {
    if (statusTooltipOpen) {
      setStatusTooltipOpen(false);
    }
  };

  const { mutate: updateAccount, isSuccess: isSuccessUpdateAccount } =
    useEditAccount();

  const { mutate: updateProspect, isSuccess: isSuccessUpdateProspect } =
    useEditProspect();

  useEffect(() => {
    if (isSuccessUpdateAccount || isSuccessUpdateProspect) {
      handleCloseTooltip();
    }
  }, [isSuccessUpdateAccount, isSuccessUpdateProspect]);

  const handleUpdateStatus = (status: string) => {
    if (objectToUpdate) {
      if (type === 'account') {
        if (isExternalPropertyStatus) {
          updateAccount({
            accountId: objectToUpdate,
            externalValues: {
              [externalPropertyStatusId!]: status,
            },
          });
        } else {
          updateAccount({
            accountId: objectToUpdate,
            status: status as DefaultCompanyStatusOptions,
          });
        }
      } else {
        // 'prospect' and 'lead' both update via the same prospect API
        if (isExternalPropertyStatus) {
          updateProspect({
            prospectId: objectToUpdate,
            externalValues: {
              [externalPropertyStatusId!]: status,
            },
          });
        } else {
          updateProspect({
            prospectId: objectToUpdate,
            actualStatus: status as DefaultContactStatusOptions,
          });
        }
      }
    } else if (handleSetStatus) {
      handleSetStatus(status);
      handleCloseTooltip();
    }
  };

  return (
    <ControlledPopover
      open={statusTooltipOpen && !disabled}
      onClose={handleCloseTooltip}
      placement={placement}
      overlayProps={overlayProps}
      anchorClassName={cn(fixedWidth && 'min-w-0')}
      className="max-w-110 rounded-2xl"
      content={
        <StatusTooltipPicker
          onClick={handleCloseTooltip}
          statuses={statuses}
          currentStatus={currentOption ?? undefined}
          setStatus={handleUpdateStatus}
        />
      }
    >
      {nullAsTagPicker ? (
        //To show a different type that is the one that looks like a tag
        <StatusTagButtonWrapper
          onClick={() => {
            if (!disabled) {
              setStatusTooltipOpen((prev) => !prev);
            }
          }}
          className={statusTooltipOpen || !!currentOption ? 'opened' : ''}
        >
          <StatusSettingsIcon
            className="status-settings-icon"
            color={
              statusTooltipOpen || !!currentOption
                ? colors.mainDark
                : colors.white
            }
          />
          <p
            className={cn(
              'truncate text-xs leading-4',
              statusTooltipOpen || !!currentOption
                ? 'text-gray-900'
                : 'text-white',
            )}
          >
            Status{' '}
            {currentOption &&
              `(${
                statuses.find((status) => status.option === currentOption)
                  ?.label
              })`}
          </p>
          {statusTooltipOpen ? (
            <IconTopArrow className="tag-arrow" color={colors.grey11} />
          ) : (
            <IconBottomArrow
              className="tag-arrow"
              color={currentOption ? colors.grey11 : colors.white}
            />
          )}
        </StatusTagButtonWrapper>
      ) : (
        <CronoStatusWrapper
          style={{
            background: backgroundColor,
            color: textColor,
            cursor: disabled ? 'default' : 'pointer',
            width: fixedWidth ? 130 : 'fit-content',
            ...(fixedWidth && { maxWidth: '100%' }),
            ...style,
          }}
          onClick={() => {
            if (!disabled) {
              setStatusTooltipOpen((prev) => !prev);
            }
          }}
        >
          <OverlayTooltip
            content={currentStatus?.label}
            overlayProps={overlayProps}
            whenTruncated
          >
            <p className="truncate text-xs leading-4 font-medium">
              {currentStatus?.label ?? 'No Value'}
            </p>
          </OverlayTooltip>
        </CronoStatusWrapper>
      )}
    </ControlledPopover>
  );
};

export default CronoStatus;

export const StatusTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    arrow={props.arrow ? props.arrow : false}
    {...props}
    classes={{ popper: className }}
  />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'white',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 440,
    width: 'fit-content',
    border: '1px solid #dadde9',
    borderRadius: 16,
    WebkitBoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
    MozBoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
    OBoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
    BoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
  },
}));
